/**
 * Gravação de áudio no consultório (microfone) para transcrição + evolução com IA.
 */

export type ConsultRoomRecordingPhase =
  | 'idle'
  | 'recording'
  | 'processing'
  | 'done'
  | 'failed';

export function pickConsultAudioMimeType(): string {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  for (const t of types) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
  }
  return 'audio/webm';
}

const STOP_TIMEOUT_MS = 25_000;

export class ConsultRoomAudioRecorder {
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];

  isRecording(): boolean {
    const s = this.mediaRecorder?.state;
    return s === 'recording' || s === 'paused';
  }

  async start(): Promise<void> {
    if (this.isRecording()) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Seu navegador não permite captura de microfone.');
    }
    this.chunks = [];
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    const mimeType = pickConsultAudioMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
    this.mediaRecorder.ondataavailable = (ev) => {
      if (ev.data?.size) this.chunks.push(ev.data);
    };
    this.mediaRecorder.start(1000);
  }

  private stopTracks(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private buildBlob(mimeHint?: string): Blob {
    const mime = mimeHint || this.mediaRecorder?.mimeType || pickConsultAudioMimeType();
    return new Blob(this.chunks, { type: mime });
  }

  async stop(): Promise<Blob> {
    const recorder = this.mediaRecorder;
    if (!recorder) {
      const b = this.buildBlob();
      this.chunks = [];
      this.stopTracks();
      if (!b.size) throw new Error('Nenhum áudio gravado.');
      return b;
    }

    if (recorder.state === 'inactive') {
      const b = this.buildBlob();
      this.chunks = [];
      this.mediaRecorder = null;
      this.stopTracks();
      if (!b.size) throw new Error('Nenhum áudio gravado.');
      return b;
    }

    const blob = await new Promise<Blob>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error('Tempo esgotado ao finalizar a gravação.'));
      }, STOP_TIMEOUT_MS);

      recorder.onstop = () => {
        window.clearTimeout(timeout);
        const b = this.buildBlob(recorder.mimeType);
        this.chunks = [];
        this.mediaRecorder = null;
        this.stopTracks();
        if (!b.size) {
          reject(new Error('Gravação vazia. Verifique o microfone e tente de novo.'));
          return;
        }
        resolve(b);
      };
      recorder.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('Erro ao finalizar gravação.'));
      };
      try {
        if (recorder.state === 'recording' && typeof recorder.requestData === 'function') {
          recorder.requestData();
        }
        recorder.stop();
      } catch (e) {
        window.clearTimeout(timeout);
        reject(e);
      }
    });

    return blob;
  }

  dispose(): void {
    try {
      if (this.mediaRecorder?.state === 'recording') {
        this.mediaRecorder.stop();
      }
    } catch {
      /* noop */
    }
    this.mediaRecorder = null;
    this.chunks = [];
    this.stopTracks();
  }
}
